import type { CheckoutModalScreen } from '@/components/modules/checkout/CheckoutModal/CheckoutModal.hooks';
import { BLANK_VALUE } from '@/constants/analytics';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { getPaymentMethodLabel } from '@/helpers/analyticsTS';
import {
  bookingFlowTrackingSession,
  buyBundleFlowTrackingSession,
  paymentActivationFlowTrackingSession,
} from '@/helpers/checkoutFlowTrackingSession';
import { track } from '@/helpers/tracking';
import { paymentMethodFailedErrorTypeSchema } from '@/types/analytics/error-types';
import { SelectedPaymentMethod } from '@/types/checkout';
import type { Catalogue } from '@bokadirekt/tracking-schema/marketplace';

type CheckoutFlow = Catalogue['Payment Method Selected']['flow'];
type SelectedMethod = Catalogue['Payment Method Selected']['payment_method'];
type CardPaymentMethod = Catalogue['Payment Method Initiated']['payment_method'];

/** The checkout an event is sent from; `company` is left out when buying a universal card. */
export type PaymentMethodCheckout<F extends CheckoutFlow = CheckoutFlow> = {
  flow: F;
  company?: { id: number; name: string };
};

export type CardCheckout = PaymentMethodCheckout<Catalogue['Payment Method Initiated']['flow']>;

const SAVED_CARD_METHODS: SelectedMethod[] = ['card', 'gift_card', 'wellness_card', 'value_card'];

function getCheckoutProperties<F extends CheckoutFlow>({ flow, company }: PaymentMethodCheckout<F>) {
  const bookingSessionId = flow === 'booking' && bookingFlowTrackingSession.getId();
  const paymentActivationSessionId = flow === 'payment_activation' && paymentActivationFlowTrackingSession.getId();
  const buyBundleSessionId = flow === 'bundle' && buyBundleFlowTrackingSession.getId();

  return {
    flow,
    ...(bookingSessionId && { booking_session_id: bookingSessionId }),
    ...(paymentActivationSessionId && { payment_activation_session_id: paymentActivationSessionId }),
    ...(buyBundleSessionId && { buy_bundle_session_id: buyBundleSessionId }),
    ...(company && { place_id: String(company.id), place_name: company.name }),
  };
}

export function getCardPaymentMethod(screen: CheckoutModalScreen): CardPaymentMethod | undefined {
  switch (screen) {
    case 'applyGiftcard':
      return 'gift_card';
    case 'applyWellnesscard':
      return 'wellness_card';
    case 'applyValuecard':
      return 'value_card';
    default:
      return undefined;
  }
}

export function trackPaymentMethodInitiated(paymentMethod: CardPaymentMethod, checkout: CardCheckout) {
  track('Payment Method Initiated', { payment_method: paymentMethod, ...getCheckoutProperties(checkout) });
}

export function trackPaymentMethodCancelled(paymentMethod: CardPaymentMethod, checkout: CardCheckout) {
  track('Payment Method Cancelled', { payment_method: paymentMethod, ...getCheckoutProperties(checkout) });
}

/** `errorCode` is the server's `giftCardUsageError`; it is sent only if the plan lists it. */
export function trackPaymentMethodFailed(paymentMethod: CardPaymentMethod, checkout: CardCheckout, errorCode?: string) {
  track('Payment Method Failed', {
    error_type: paymentMethodFailedErrorTypeSchema.catch('unknown').parse(errorCode),
    payment_method: paymentMethod,
    ...getCheckoutProperties(checkout),
  });
}

/** `isSavedCard` is sent for card methods only: a card on the account before this checkout began. */
export function trackPaymentMethodSelected(
  paymentMethod: SelectedMethod,
  checkout: PaymentMethodCheckout,
  isSavedCard: boolean,
) {
  track('Payment Method Selected', {
    payment_method: paymentMethod,
    ...(SAVED_CARD_METHODS.includes(paymentMethod) && { is_saved_card: isSavedCard }),
    ...getCheckoutProperties(checkout),
  });
}

function isSameOption(selected: SelectedPaymentMethod, method: SelectedPaymentMethod) {
  if (method.type === CHECKOUT_PAYMENT_METHOD.STORED_COF) {
    return selected.type === CHECKOUT_PAYMENT_METHOD.STORED_COF && selected.id === method.id;
  }
  return selected.type === method.type;
}

/**
 * The user's own change of the selected option, from `selected` to `method`. A stored
 * card counts as its own option, so switching between two cards is a change too.
 * `addedCardIds` holds the payment cards added during this checkout.
 */
export function trackPaymentMethodChange(
  selected: SelectedPaymentMethod,
  method: SelectedPaymentMethod,
  checkout: PaymentMethodCheckout,
  addedCardIds: ReadonlySet<string>,
) {
  const paymentMethod = getPaymentMethodLabel(method.type);
  if (paymentMethod === BLANK_VALUE || isSameOption(selected, method)) return;

  trackPaymentMethodSelected(
    paymentMethod,
    checkout,
    method.type === CHECKOUT_PAYMENT_METHOD.STORED_COF && !addedCardIds.has(method.id),
  );
}
